import {Modal} from "antd";
import "./edit-segment-modal.css";
import {useEffect, useMemo, useState} from "react";
import {MessageOutlined} from "@ant-design/icons";
import SegmentConditionBuilder from "../newSegment/SegmentConditionBuilder";
import SegmentDetailsStep from "../newSegment/SegmentDetailsStep";
import {ConditionsProps, defaultConditions} from "../../../utils/segmentBuilder";
import useUserContext from "../../../hooks/useUserContext";
import useAppContext from "../../../hooks/useAppContext";
import {updateSegment} from "../../../api/updateSegment";
import useToast from "../../../hooks/useToast";
import {ToastSkin} from "../../../context/ToastContext";
import {deleteSegment} from "../../../api/deleteSegment";

interface EditSegmentModalProps {
    segmentId: number | null;
    onClose: () => void;
}

const EditSegmentModal = ({ segmentId, onClose }: EditSegmentModalProps) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [initialSegmentState, setInitialSegmentState] = useState([defaultConditions]);
    const { selectedDomain } = useUserContext();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { data: { segments }, actions: { loadSegments }} = useAppContext();
    const { showToast, closeToast } = useToast();

    const segment = useMemo(() => {
        if(segmentId) {
            const segment = segments.data.find((s: any) => s.id === segmentId);
            setTitle(segment.title);
            setDescription(segment.description);
            setInitialSegmentState(segment.conditions);
            return segment;
        } else return null;
    }, [segmentId]);

    const isOpen = !!segment;

    const deleteSegmentAction = async () => {
        const response: any = selectedDomain && segmentId && await deleteSegment({ domain: selectedDomain, segmentId });
        if(response?.data === 'success') {
            loadSegments();
            showToast({ skin: ToastSkin.approval, text: 'The segment has been deleted successfully.' });
            closeModal();
        }
    }

    const deleteSegmentClick = async () => {
        showToast({ skin: ToastSkin.danger, text: "Are you sure you want to delete this segment? If it's used in any campaigns, it will be removed from their segment lists.", buttons: [
                { text: 'Yes', onClick: deleteSegmentAction},
                { text: 'No, Cancel', onClick: closeToast }
            ]});
    }

    useEffect(() => {
        if(!isOpen) {
            setTitle('');
            setDescription('');
        }
    }, [isOpen]);

    const onSubmitEditSegment = async (segmentState: ConditionsProps[]) => {
        if(!selectedDomain || !segmentId) return false;
        setIsSubmitting(true);
        const response = await updateSegment({ domain: selectedDomain, segmentId, title, description, conditions: segmentState });
        setIsSubmitting(false);
        const isOk = response.status === 200 && response.data === 'success';
        isOk && loadSegments();
        showToast({ skin: ToastSkin.approval, text: 'Segment updated successfully.' });
        closeModal();
        return isOk;
    }

    const closeModal = () => {
        !isSubmitting && onClose();
    }

    const steps = [
        {
            title: 'Segment Details',
            icon: <MessageOutlined />,
        },
        {
            title: 'Conditions',
            icon: <MessageOutlined />,
        },
    ];

    return (
        <Modal open={isOpen} onCancel={closeModal} destroyOnHidden={true} className="segment-edit-modal" width={950}
               // height={window.innerHeight * 0.85}
               // modalRender={(modalNode) => modalNode}
               okButtonProps={{ style: { display: 'none' }}} cancelButtonProps={{ style: { display: 'none' }}}>
            <div className="modal-header">
                <div className="title">Edit Segment</div>
                <div className="description">Edit your segment's name, description, or conditions as needed.</div>
            </div>
            <SegmentDetailsStep isNew={false} isSubmitting={isSubmitting} onClose={closeModal} title={title} setTitle={setTitle} description={description} setDescription={setDescription} />
            <SegmentConditionBuilder isNew={false} isSubmitting={isSubmitting} isOpen={isOpen} initialSegmentState={initialSegmentState} onSubmit={onSubmitEditSegment} deleteSegment={deleteSegmentClick} />
        </Modal>
    )
}

export default EditSegmentModal;